import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Field, VStack, Text, Badge, HStack } from '@chakra-ui/react';
import { InputField } from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { Button } from '@themes/aegix';
import { SelectInput } from '@themes/aegix';
import { RoleRow } from '../../types/role';
import { useCloneGroup, useOrganizations } from '../../hooks/queries';
import { toaster } from '../ui/toaster';

interface CloneRoleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sourceRole: RoleRow | null;
}

export default function CloneRoleDialog({
  isOpen,
  onClose,
  sourceRole,
}: CloneRoleDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [errors, setErrors] = useState<{
    name?: string;
    organization?: string;
    form?: string;
  }>({});

  const cloneMutation = useCloneGroup();
  const { data: orgOptions = [], isError: orgError } = useOrganizations();

  const organizationOptions = orgOptions.map((o) => ({
    label: o.label,
    value: String(o.id),
  }));

  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription(
        sourceRole ? t('forms.clonedFrom', 'Cloned from {{name}}', { name: sourceRole.display_name }) : '',
      );
      setOrganizationId('');
      setErrors({});
    }
  }, [isOpen, sourceRole, t]);

  const handleSubmit = async () => {
    if (!sourceRole) return;

    const validationErrors: { name?: string; organization?: string } = {};
    if (!name.trim()) {
      validationErrors.name = t('forms.roleNameRequired', 'Role name is required');
    }
    if (!organizationId) {
      validationErrors.organization = t('forms.organizationRequired', 'Organization is required');
    }
    if (validationErrors.name || validationErrors.organization) {
      setErrors(validationErrors);
      return;
    }

    try {
      await cloneMutation.mutateAsync({
        id: Number(sourceRole.id),
        data: {
          name: name.trim(),
          organization_id: Number(organizationId),
          description: description.trim(),
        },
      });

      toaster.create({
        title: t('forms.roleClonedSuccessfully', 'Role cloned successfully'),
        type: 'success',
        duration: 4000,
      });

      onClose();
    } catch (err) {
      const parsed = err as Error & {
        response?: { data?: { detail?: string; name?: string[] } };
      };
      const errorMessage =
        parsed?.response?.data?.detail ||
        parsed?.response?.data?.name?.[0] ||
        parsed?.message ||
        t('forms.failedToCloneRole', 'Failed to clone role');
      setErrors({ form: errorMessage });
      toaster.create({
        title: t('common.error', 'Error'),
        description: errorMessage,
        type: 'error',
        duration: 4000,
      });
    }
  };

  if (!sourceRole) return null;

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={t('forms.cloneRole', 'Clone Role')}
      size="md"
      closeOnOutside={false}
      closeOnEsc={false}
      footer={
        <HStack gap={4} width="100%">
          <Button visual="secondary" onClick={onClose} flex={1} data-e2e="cancel-button">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            visual="primary"
            onClick={handleSubmit}
            flex={1}
            disabled={cloneMutation.isPending}
            data-e2e="clone-role-button">
            {cloneMutation.isPending ? t('forms.cloning', 'Cloning...') : t('forms.cloneRole', 'Clone Role')}
          </Button>
        </HStack>
      }
    >
      <VStack gap={4} align="stretch" data-e2e="clone-role-form">
        <Box
          p={3}
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        >
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            {t('forms.cloningFrom', 'Cloning from:')}
          </Text>
          <HStack>
            <Text fontWeight="bold">{sourceRole.display_name}</Text>
            {sourceRole.is_system_role && (
              <Badge colorScheme="blue" size="sm">
                {t('common.system', 'System')}
              </Badge>
            )}
          </HStack>
          <Text fontSize="sm" color="gray.600" mt={1}>
            {t('forms.permissionsCountWillBeCopied', '{{count}} permissions will be copied', { count: sourceRole.permissions_count })}
          </Text>
        </Box>

        {errors.form && (
          <Box
            p={3}
            bg="red.50"
            borderRadius="md"
            border="1px solid"
            borderColor="red.200"
          >
            <Text fontSize="sm" color="red.700">
              {errors.form}
            </Text>
          </Box>
        )}

        <Field.Root invalid={!!errors.name} required>
          <Field.Label htmlFor="clone-role-name">
            {t('forms.newRoleName', 'New Role Name')}
            <Field.RequiredIndicator />
          </Field.Label>
          <InputField
            id="clone-role-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrors((prev) => ({ ...prev, name: undefined, form: undefined }));
            }}
            placeholder={t('forms.enterNewRoleNamePlaceholder', 'Enter new role name')}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- modal dialog: focus lands on the first (and only) required field when the user opens the dialog, which is the expected dialog-focus behaviour rather than an on-load focus steal
            autoFocus
            data-e2e="enter-new-role-name-input"
            aria-describedby={errors.name ? 'clone-role-name-error' : undefined}
          />
          {errors.name && (
            <Field.ErrorText id="clone-role-name-error">
              {errors.name}
            </Field.ErrorText>
          )}
        </Field.Root>

        <VStack gap={1.5} align="stretch">
          <SelectInput
            id="clone-role-organization"
            name="clone-role-organization"
            value={organizationId}
            label={`${t('admin.organization', 'Organization')} *`}
            option={organizationOptions}
            onChange={(value) => {
              setOrganizationId(value);
              setErrors((prev) => ({
                ...prev,
                organization: undefined,
                form: undefined,
              }));
            }}
            invalid={!!errors.organization}
            error={errors.organization}
            e2eTitle="organization_id"
          />
          {orgError && (
            <Text fontSize="xs" color="text.danger">
              {t('forms.failedLoadOrganizations', 'Failed to load organizations. Please try refreshing.')}
            </Text>
          )}
        </VStack>

        <Field.Root>
          <Field.Label htmlFor="clone-role-description">{t('forms.description', 'Description')}</Field.Label>
          <InputField
            id="clone-role-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('forms.enterDescriptionOptionalPlaceholder', 'Enter description (optional)')}
            data-e2e="enter-description-optional-input" />
        </Field.Root>
      </VStack>
    </DrawerComponent>
  );
}
